/**
 * Cluster utilities for InsightsAnalysis source management.
 * Recalculates aggregated insight metrics after add/remove operations.
 */

interface InsightSource {
  actorId: string;
  actorName: string;
  audienceId: string;
  originalItemId: string;
  originalDescription: string;
  originalWeight: number;
  similarityScore: number;
}

interface AggregatedInsight {
  id: string;
  category: string;
  canonicalDescription: string;
  cumulativeWeight: number;
  frequency: number;
  avgWeight: number;
  sources: InsightSource[];
  questionText?: string;
  [key: string]: any;
}

/**
 * Recalculate cluster metrics after source add/remove.
 */
export function recalculateCluster(insight: AggregatedInsight): AggregatedInsight {
  const sources = insight.sources || [];
  const uniqueActors = new Set(sources.map(s => s.actorId));
  const cumulativeWeight = sources.reduce((sum, s) => sum + (s.originalWeight || 0), 0);
  const frequency = uniqueActors.size;

  return {
    ...insight,
    cumulativeWeight,
    frequency,
    avgWeight: frequency > 0 ? cumulativeWeight / frequency : 0,
  };
}

/**
 * Remove a source from a cluster and recalculate.
 * Returns null if cluster becomes empty.
 */
export function removeSourceFromCluster(
  insight: AggregatedInsight,
  originalItemId: string,
): AggregatedInsight | null {
  const newSources = insight.sources.filter(s => s.originalItemId !== originalItemId);
  if (newSources.length === 0) return null;
  return recalculateCluster({ ...insight, sources: newSources });
}

/**
 * Add a source to a cluster and recalculate.
 */
export function addSourceToCluster(
  insight: AggregatedInsight,
  source: InsightSource,
): AggregatedInsight {
  // Prevent duplicates
  if (insight.sources.some(s => s.originalItemId === source.originalItemId)) {
    return insight;
  }
  return recalculateCluster({
    ...insight,
    sources: [...insight.sources, source],
  });
}

/**
 * Apply cluster changes to the full aggregatedInsights map.
 * Handles removing empty clusters.
 */
export function updateInsightInCategory(
  aggregatedInsights: Record<string, AggregatedInsight[]>,
  category: string,
  insightId: string,
  updatedInsight: AggregatedInsight | null,
): Record<string, AggregatedInsight[]> {
  const categoryInsights = aggregatedInsights[category] || [];

  if (updatedInsight === null) {
    // Remove the cluster
    return {
      ...aggregatedInsights,
      [category]: categoryInsights.filter(i => i.id !== insightId),
    };
  }

  return {
    ...aggregatedInsights,
    [category]: categoryInsights.map(i => i.id === insightId ? updatedInsight : i),
  };
}
